import { markResolved } from './diceTrace.ts';
/**
 * RWBR — PIT TURNS (rulebook p.xi–xiv).
 *
 * Pit turns are pre-determined by the length of the race and are resolved
 * instead of, not alongside, the RACE CAM (p.xi). The same black die and two
 * metallic dice are rolled, but read completely differently.
 *
 * PROCEDURE, as summarised on p.xiii–xiv:
 *
 *   1) "The METALLIC dice determines the PIT re-ordering… if you roll a '1' and
 *       a '4' on the metallic dice, you switch the drivers in those boxes, in
 *       BOTH groups.  If you roll doubles on the metallic dice, a pit rules
 *       violation has been committed by the driver(s) in the box indicated, in
 *       the group(s) eligible for the FAST PIT only.  When a pit rules violation
 *       occurs, the driver card(s) is(are) moved to the end of the bottom group."
 *
 *   2) "The BLACK die determines which group gets to go for the FAST PIT; 1-2
 *       indicates the TOP group and LEADER, 3-4 is the MIDDLE group, 5, BOTH
 *       groups and the race LEADER, 6, TV drivers in TOP or MIDDLE groups, and
 *       the race LEADER, if he is on the TV List.  Using the METALLIC dice,
 *       conduct the FAST PIT rolls for each driver in the eligible group(s)…
 *       Place a performance chip on the card of each driver who rolls within his
 *       FAST PIT range."
 *
 *   3) "After all FAST PIT rolls are conducted, swap all drivers who have at
 *       least ONE performance chip to the FRONT of their respective groups, in
 *       descending order.  Non-chip drivers drop back.  NOTE that even if a
 *       driver earned the chip PRIOR to the PIT TURN, he still gets to move up."
 *       (p.xii adds: "Do this for BOTH the TOP and MIDDLE groups, even if one or
 *       the other was not indicated in the FAST PIT sequence.  The drivers in the
 *       bottom group are NOT re-ordered.")
 *
 *   4) "The FINAL step is to roll black die again, to indicate which driver in
 *       the TOP group gets to challenge race LEADER using GAS PEDAL grade, no
 *       defense.  NOTE that the current race leader may choose to spend a
 *       performance chip (if he has one) to maintain his LEADER status."
 *
 * PURITY. The `RaceState` handed in is never touched. The result carries a new
 * running order and the events that produced it, in the order they happened.
 */

import type { Car, Group, RaceState } from './types.ts';
import { groupOf } from './types.ts';
import type { Dice } from './dice.ts';
import type { ChipDelta, DiceRecord, Modifier, PositionDelta, RuleEvent } from './ruleEvent.ts';
import { diffOrder, fieldFromState, nameOf, renumber } from './ruleEvent.ts';
import { gainChip, parkChips, scopeChips, traitChipsOf } from './chips.ts';
import { MIDDLE_BOX_POSITION, TOP_BOX_POSITION } from './challenge.ts';
import { fillGaps, type FillGapResult } from './fillGap.ts';
import { rollPitViolation, type PitPenaltyKind, type PitViolationResult } from './pitViolations.ts';
import { LEADER_GROUP_FOR_LPCG, rollPitTrouble, type PitTroubleResult } from './pitTrouble.ts';
import {
  LapsDownTracker,
  moveToLastOfLeadLap,
  sendLapDown,
} from './lapsDown.ts';

// ---------------------------------------------------------------------------
// Citations
// ---------------------------------------------------------------------------

export const CITE = {
  pitTurn: 'RB/xi — PIT TURNS',
  reorderDice: 'RB/xi — "the metallic dice are used to re-order the drivers in BOTH the TOP and MIDDLE groups by simply switching positions."',
  violation: 'RB/xi-xii — "If you roll doubles … the driver(s) in that numbered box are considered to have committed a pit rules violation, and must move to the end of the BOTTOM group. Use the black die result for FAST PIT to determine which group(s) had driver(s) commit pit violations."',
  fastPitGroups: 'RB/xii & RB/xiii(2) — the black die names the FAST PIT group(s).',
  fastPitRoll: 'RB/xii — "each driver in that group will get a roll using the metallic dice, comparing the number rolled to his \'fast pit\' rating."',
  reorderChips: 'RB/xii & RB/xiii(3) — chipped drivers move to the front of BOTH the TOP and MIDDLE groups; the BOTTOM group is not re-ordered.',
  leadChallenge: 'RB/xiii-xiv(4) — "roll black die again, to indicate which driver in the TOP group gets to challenge race LEADER using GAS PEDAL grade, no defense."',
  leaderChip: 'RB/xiv — "the current race leader may choose to spend a performance chip (if he has one) to maintain his LEADER status."',
  chipsBottom: 'RB/xv — "performance chips cannot be carried by drivers in the BOTTOM group."',
} as const;

// ---------------------------------------------------------------------------
// EZRA: what the two pit charts hang off
// ---------------------------------------------------------------------------

/**
 * ═════ THE TRIGGERS, SETTLED AGAINST PRIMARY TEXT ═════
 *
 * These were the open question in the handoff, which recorded the EZRA Track
 * Pack's version: *"result number 11 to indicate a Pit Violation & 12 for
 * designating Pit TROUBLE"* — both on the Fast Pit roll. Checked against
 * EZRA-S2 §3.9 and Appendices B1/B2, that is **half right**, and D-EZ-7 makes
 * Season 2 govern where they differ.
 *
 * PIT TROUBLE — S2 prints the trigger outright:
 *   *"When checking for a Fast Pit if the resulting dice roll is 12, the Pit
 *    Crew experiences TROUBLE in the pit. Roll 2D6 and read the results on the
 *    PIT STOP TROUBLE CHART"*
 *   So: Fast Pit roll of 12. Same as the Track Pack. EZRA caps Fast Pit ratings
 *   at 9, so a 12 can never also be a success.
 *
 * PIT VIOLATION — S2 says only:
 *   *"Pit Violation Chart. For use when the RACE CAM DICE result in a Pit
 *    Violation. See Appendix B1 for Results."*
 *   It names the trigger without defining it, and there is **no Pit Violation
 *   row on the Race Cam results table** (2 RARE … 12 TROUBLE*). So S2 is silent
 *   on the mechanism, and the standing rule applies: where an EZRA add-on is
 *   silent, the base RWBR rulebook governs. RB/xi-xii is the only place in
 *   either ruleset where the dice "result in a Pit Violation":
 *     *"If you roll doubles [on the metallic dice in a pit turn] … the
 *      driver(s) in that numbered box are considered to have committed a pit
 *      rules violation."*
 *   And RB/xi has already told us those metallic dice ARE the Race Cam dice,
 *   read differently on a pit turn.
 *
 *   So the trigger is UNCHANGED from the base game. What EZRA replaces is the
 *   PENALTY: instead of the flat "move to the end of the BOTTOM group", roll
 *   Appendix B1 and serve what it prints.
 *
 *   The Track Pack's "11 = Pit Violation" on the Fast Pit roll is REJECTED. It
 *   is not in S2, and adopting it would make violations fire on a per-driver
 *   roll rather than a per-turn one — a different rate by an order of magnitude.
 */
export const CITE_EZRA_PIT_TRIGGERS = {
  violation:
    'EZRA-S2 §3.9 — "For use when the RACE CAM DICE result in a Pit Violation. See Appendix B1 for ' +
    'Results." S2 names the trigger but does not define it and the Race Cam table has no Pit ' +
    'Violation row, so RB/xi-xii governs: doubles on the metallic dice, in the FAST-PIT-eligible ' +
    'group(s). EZRA replaces the penalty only.',
  trouble:
    'EZRA-S2 §3.9 — "When checking for a Fast Pit if the resulting dice roll is 12, the Pit Crew ' +
    'experiences TROUBLE in the pit. Roll 2D6 and read the results on the PIT STOP TROUBLE CHART".',
  trackPackRejected:
    'EZRA Track Pack\'s "result number 11 to indicate a Pit Violation" is rejected under D-EZ-7: ' +
    'S2 §3.9 points at its own appendices and gives a different trigger. The 12 = Pit TROUBLE half ' +
    'of the Track Pack rule is confirmed by S2 and is used.',
} as const;

/**
 * Where each Appendix B1 penalty puts the car on a board that has no laps-down
 * sub-group and no pit lane.
 *
 * `printed` marks the ones B1 states in board terms we can honour exactly.
 * Everything else is a reading or a degradation, and every one of them is
 * reported on the result rather than applied quietly — see
 * `PitViolationOutcome.degraded`.
 */
export const PENALTY_PLACEMENT: Readonly<
  Record<
    PitPenaltyKind,
    {
      green: PitPlacement;
      yellow: PitPlacement;
      /** Is the GREEN-flag placement printed in board terms, or is it a reading? */
      printedGreen: boolean;
      /** Is the YELLOW-flag placement printed in board terms? */
      printedYellow: boolean;
      why: string;
    }
  >
> = Object.freeze({
  'tail-of-field': {
    green: 'end-of-bottom',
    yellow: 'end-of-bottom',
    printedGreen: true,
    printedYellow: true,
    why: '"Restart at the tail of the field" is the end of the BOTTOM stack, exactly.',
  },
  'pass-through-or-tail': {
    green: 'last-in-current-group',
    yellow: 'end-of-bottom',
    // The two halves of this row have DIFFERENT provenance and are tracked
    // separately, otherwise the audit over-reports: under yellow this row is
    // printed exactly, and it is the commonest row on the chart.
    printedGreen: false,
    printedYellow: true,
    why:
      'RULED 16 Aug 2026 — D-EZ-10 is closed, and this reading stands. The yellow half is printed ' +
      '("Restart at the tail of the field"). The green half — "Must pass through pits under green" — ' +
      'costs track position that B1 never quantifies, and is read as EZRA\'s own default displacement, ' +
      'printed on the B2 header: "drivers are displaced to the Last Position in the Current Group". ' +
      'TWO THINGS NOW BACK IT. (1) Collapsing it to end-of-bottom would erase the distinction B1 draws ' +
      'inside a single sentence, between the green and yellow halves of its commonest row. (2) Since ' +
      'the laps-down group shipped, EZRA\'s own contrast is visible on the board: it prints ' +
      '"PENALTY: 1 lap" outright on rows 22 and 46, and those now cost a real lap. Where EZRA means a ' +
      'lap it says so — and it does not say so on any of the twelve pass-through rows.',
  },
  'lap-penalty': {
    green: 'laps-down',
    yellow: 'laps-down',
    printedGreen: true,
    printedYellow: true,
    why:
      'EXACT, as of the laps-down sub-group shipping. "PENALTY: 1 lap" means the car loses a lap, ' +
      'and there is now a place on the board that means that: the end of the LDSG. This row was ' +
      'DEGRADED to the end of the BOTTOM group for as long as the board had nowhere to put a lapped ' +
      'car, which was strictly milder than the printed rule and was reported as such on every result.',
  },
  'second-stop': {
    green: 'end-of-bottom',
    yellow: 'end-of-bottom',
    printedGreen: false,
    printedYellow: false,
    why:
      'RULED under D-EZ-1, and NOT a degradation — the label this row used to carry was wrong. ' +
      'EZRA-S2 B1 prints "PENALTY: Must come in" and stops there: it names an ACTION, not a place ' +
      'on the board. Where an EZRA add-on is silent the base rulebook governs, and RB\'s pit ' +
      'violation penalty is the end of the BOTTOM group. So this IS the printed answer, arrived at ' +
      'through the rule that exists for exactly this case. ' +
      '⚠ IT DOES NOT MOVE TO THE LAPS-DOWN GROUP now that one exists. Nothing in S2 says a second ' +
      'stop costs a lap; EZRA prints "PENALTY: 1 lap" outright where it means one (rows 22, 46). ' +
      'Sending this car a lap down because the board finally has somewhere to put it would be ' +
      'inventing a harsher penalty than the sheet prints. ' +
      '(EZRA-TP\'s generation of this chart prints "2nd Pit Stop to ELDG" — the End of the Lap Down ' +
      'Group. That is a DIFFERENT chart: CROSS_REFERENCE C-pit records that the MH and EZRA-TP ' +
      'charts "are two different generations of the same chart and must not be merged", and D-EZ-1 ' +
      'gives S2 the casting vote. Recorded here so the next reader does not rediscover it and ' +
      'assume it was missed.)',
  },
  'stop-and-go': {
    green: 'end-of-bottom',
    yellow: 'end-of-bottom',
    printedGreen: false,
    printedYellow: false,
    why:
      'RULED under D-EZ-1, on the same reasoning as second-stop, and likewise NOT a degradation. ' +
      '"PENALTY: Stop and Go" names an action and no board position, so the base rulebook\'s ' +
      'end-of-BOTTOM-group governs. It does NOT move to the laps-down group: a stop and go is the ' +
      'MILDEST penalty in real NASCAR and EZRA prints an explicit "1 lap" on the rows where it ' +
      'means one.',
  },
  discretion: {
    green: 'last-in-current-group',
    yellow: 'end-of-bottom',
    printedGreen: false,
    printedYellow: false,
    why:
      'Never reached: pitViolations.ts resolves discretion to pass-through-or-tail before it gets ' +
      'here. Present so the map is total.',
  },
  'feud-roll': {
    green: 'none',
    yellow: 'none',
    printedGreen: true,
    printedYellow: true,
    why: 'Row 51 prints "(Roll for Feud)" and no positional penalty at all. The car does not move.',
  },
});

/** Where a penalised car ends up. */
export type PitPlacement = 'end-of-bottom' | 'last-in-current-group' | 'laps-down' | 'none';

/**
 * Move a car to the last position of its own group, in place.
 *
 * Group geometry is fixed (LEADER 1, TOP 2-7, MIDDLE 8-19, BOTTOM 20+) but a
 * short field can end before a boundary, so the target is clamped to the field.
 * The LEADER is not a group; he is placed at the back of TOP per
 * `LEADER_GROUP_FOR_LPCG`.
 */
export function moveToLastOfGroup(order: Car[], driverId: string): { from: number; to: number } | null {
  const i = order.findIndex((c) => c.card.driverId === driverId);
  if (i === -1) return null;
  const from = i + 1;
  const group: Group = from === 1 ? LEADER_GROUP_FOR_LPCG : groupOf(from);

  let target: number;
  if (group === 'top') target = 7;
  else if (group === 'middle') target = 19;
  else target = order.length;
  target = Math.min(target, order.length);
  if (target === from) return { from, to: from };

  const [car] = order.splice(i, 1);
  order.splice(target - 1, 0, car);
  return { from, to: target };
}

/** Send a car to the end of the BOTTOM stack, in place. Chips do not travel (p.xv). */
function moveToEndOfBottom(order: Car[], driverId: string): number | null {
  const i = order.findIndex((c) => c.card.driverId === driverId);
  if (i === -1) return null;
  const from = i + 1;
  const [car] = order.splice(i, 1);
  order.push(parkChips(car).car);
  return from;
}

/** p.xi — the printed schedule: turn 7, then every tenth turn. */
export const PIT_TURN_SCHEDULE = [7, 17, 27, 37, 47, 57] as const;

/** Which of those fall inside a race of `totalTurns` turns. */
export function pitTurnsFor(totalTurns: number): number[] {
  return PIT_TURN_SCHEDULE.filter((t) => t <= totalTurns);
}

// ---------------------------------------------------------------------------
// The black die
// ---------------------------------------------------------------------------

export type FastPitScope = 'top+leader' | 'middle' | 'both+leader' | 'tv+leader-if-tv';

/** p.xii — 1-2 TOP + LEADER · 3-4 MIDDLE · 5 BOTH + LEADER · 6 TV cars + LEADER if TV. */
export function fastPitScope(black: number): FastPitScope {
  if (black <= 2) return 'top+leader';
  if (black <= 4) return 'middle';
  if (black === 5) return 'both+leader';
  return 'tv+leader-if-tv';
}

/** Does the FAST PIT scope reach into this group's numbered boxes? */
function scopeCoversGroup(scope: FastPitScope, group: 'top' | 'middle'): boolean {
  if (scope === 'top+leader') return group === 'top';
  if (scope === 'middle') return group === 'middle';
  return true; // 'both+leader' and the TV scope both look at TOP and MIDDLE
}

/** Every car eligible for a FAST PIT roll, in running order. */
export function fastPitEligible(order: readonly Car[], black: number): Car[] {
  const scope = fastPitScope(black);
  const leader = order[0];
  const top = order.slice(1, 7);
  const middle = order.slice(7, 19);
  const isTv = (c: Car) => c.tvQuality !== null;

  if (scope === 'top+leader') return [leader, ...top].filter(Boolean);
  if (scope === 'middle') return middle;
  if (scope === 'both+leader') return [leader, ...top, ...middle].filter(Boolean);
  // p.xii — "the TV drivers in the TOP and MIDDLE groups, and, if he is on the
  // TV List, the race LEADER."
  return [...(leader && isTv(leader) ? [leader] : []), ...top.filter(isTv), ...middle.filter(isTv)];
}

// ---------------------------------------------------------------------------
// Results
// ---------------------------------------------------------------------------

export interface PitViolation {
  driverId: string;
  group: 'top' | 'middle';
  box: number;
  position: number;
  chipsForfeited: number;
  /**
   * The Appendix B1 roll, when EZRA pit violations are on. Absent means the base
   * game's flat "end of the BOTTOM group" was served instead.
   */
  chart?: PitViolationOutcome;
}

/** One Appendix B1 roll and what it did to the board. */
export interface PitViolationOutcome {
  roll: PitViolationResult;
  placement: PitPlacement;
  /** True when the placement is a reading or a degradation, not printed. */
  degraded: boolean;
  why: string;
  /** Row 51 only — no positional penalty, but a feud is rolled for. */
  feudRoll: boolean;
  /** Row 51 only — the d6 of B1 Note 2 ("1-2 Feud; 3-6 No Feud"). D-EZ-89. */
  feudDie?: number;
  /** Row 51 only — whether that d6 started anything. */
  feudStarted?: boolean;
}

/** B1 Note 2 — "Roll For Feud (d6= 1-2 Feud; 3-6 No Feud)". */
export const FEUD_ROLL_MAX = 2;

/**
 * THE SPLIT FAST PIT GRADE — one roll, two thresholds (D-EZ-27).
 *
 * EZRA-FP states the design goal in its own words: *"Our goal is to eliminate
 * the decision by making the pit stop results a single die roll."* The base
 * rulebook's DOUBLE FAST PIT is a second, OPTIONAL roll with a downside —
 * RB/xvi, *"On a dice roll of '7' or worse, he loses the original chip he
 * earned."* EZRA replaces that outright and says so; the two are not
 * compatible and CROSS_REFERENCE C7 records them as irreconcilable.
 *
 * So: one 2d6 roll, read against two numbers. At or under the second grade is
 * a DOUBLE and two chips. At or under the first is one chip. Over it is
 * nothing. **No chip is ever lost** — that was the decision EZRA deleted.
 *
 * ⚠ THREE DOCUMENTS PRINT THREE DIFFERENT CONVERSION TABLES (C6), and they
 * disagree in the middle of the range:
 *
 *   EZRA-S2 (Driver Card Layout):  10+ -> 4 · 9 -> 3 · 8 -> 3 · 7 -> 2 · 6 -> 2 · <=5 -> X
 *   EZRA-TP (Split Grades):        9 -> 9/4 · 8 -> 8/3 · 7 -> 7/3 · 6 -> 6/2 · 5 -> 5/2 · "5" -> 4/x
 *   EZRA-FP (worked example):      Smith, Fast Pit 8, "Double Fast Pit grade of 5"
 *
 * A Fast-Pit-8 driver's second grade is 3, 3, and 5 in the three sources — and
 * EZRA-FP contradicts ITSELF, since its own chart section gives Smith 8/3 while
 * its worked example calls it 5. EZRA-TP's last row prints "5" on the left
 * where its own descending sequence requires 4.
 *
 * RULED (D-EZ-27, D-EZ-1): **EZRA-S2's table governs.** It is the Season 2
 * rulebook, D-EZ-1 already settles that S2 wins where the sheets overlap, and
 * it is the only one of the three that is internally consistent. The
 * disagreements are recorded here rather than averaged away.
 */
export const DOUBLE_FAST_PIT_GRADE: Readonly<Record<number, number | null>> = Object.freeze({
  12: 4, 11: 4, 10: 4, 9: 3, 8: 3, 7: 2, 6: 2,
  // "X means he will never get a Double Fast Pit."
  5: null, 4: null, 3: null, 2: null,
});

export const CITE_SPLIT_FAST_PIT =
  'EZRA-S2 Driver Card Layout + EZRA-TP "Split Grades for Fast Pits" — "The first number is the ' +
  'threshold for a routine Fast Pit Stop (earning the team a Performance Chip). The second number ' +
  'is the threshold for a Double Fast Pit Stop (earning the team two [2] Performance Chips)." ' +
  'EZRA-FP: "One roll - No decision."';

/**
 * The second grade for a card: what is PRINTED on it if anything, otherwise
 * EZRA-S2's conversion of the first grade.
 *
 * A printed value always wins, per D-021 — a mailed card supersedes anything we
 * derive. `cardImport.ts` is the only thing that sets it, and `driverGen.ts`
 * deliberately leaves it null (RULING R-7) because the split grade is an EZRA
 * add-on and a generated card should not pretend to carry one.
 */
export function doubleFastPitGrade(card: { fastPit: number; fastPitDouble: number | null }): number | null {
  if (card.fastPitDouble !== null && card.fastPitDouble !== undefined) return card.fastPitDouble;
  return DOUBLE_FAST_PIT_GRADE[card.fastPit] ?? null;
}

/**
 * THE DECIDER DIE ON A 12 (D-EZ-27) — Howard's ruling, 16 Aug 2026.
 *
 * The collision: EZRA-TP uses *"12 for designating Pit TROUBLE"*, and a 2d6 of
 * 12 is also the best roll on the table. D-EZ-9 settled it by making trouble
 * unconditional — a 12 is trouble and never also a success. That is safe and it
 * is blunt, and it only ever bites on a BASE-GAME card, because EZRA caps Fast
 * Pit at 9 and no EZRA card can hit a 12 anyway.
 *
 * Howard: *"Add a decider die to a 12 roll here to determine if it hits this."*
 * So on the one roll where a 12 would ALSO have been a hit, a die decides which
 * it is instead of trouble winning by fiat.
 *
 * ⚠ THE DECIDER DIE IS UNDEFINED CORPUS-WIDE. That is open question B4, and it
 * is why D-EZ-22 REJECTED EZRA-S2's decider-die gate on the Race Start Event —
 * a gate keyed on a die nobody has printed is a gate nobody can run. Inventing
 * one here would be the same mistake with the same name on it.
 *
 * So this does not invent a die. It reuses **the one tie-break EZRA actually
 * prints**, from its own performance-chip section: *"roll a d6, if the result
 * is an ODD number the Performance Chips are consumed by both drivers, if the
 * result is EVEN both drivers retain their Performance Chips."* A d6 read for
 * parity is EZRA's own idiom for "two outcomes, no precedence", which is
 * exactly the situation. ODD gives the stop, EVEN gives the trouble.
 *
 * SCOPE, AND WHY IT IS THIS NARROW. The die is rolled ONLY when the 12 would
 * have been a hit — i.e. a rating of 12. Everywhere else a 12 is over the
 * rating, there is nothing to decide, and trouble stands unopposed. That keeps
 * the measured pit-trouble rate (2.69% against a printed 1/36) intact for every
 * card in the league except the vanishingly rare one this rule is about.
 */
export const DECIDER_CITE =
  'D-EZ-27 (house ruling, 16 Aug 2026) — a d6 read for PARITY, which is EZRA-S2\'s own printed ' +
  'tie-break idiom ("roll a d6, if the result is an ODD number... if the result is EVEN..."). ' +
  'Rolled only where a 12 would otherwise be both a Fast Pit hit and Pit TROUBLE. ODD = the stop ' +
  'stands, EVEN = the crew drops it.';

export interface FastPitRoll {
  /** Set when a one-race POST RACE INTERVIEW grant carried this stop. */
  interviewGrant?: 'single' | 'double';
  driverId: string;
  position: number;
  rating: number;
  /** The DOUBLE threshold, or null for a card that can never double. */
  doubleRating?: number | null;
  roll: number;
  earned: boolean;
  /** EZRA's split grade: the same roll also cleared the second threshold. */
  doubled?: true;
  /** D-EZ-27 — the parity die that settled a 12 that was also a hit. */
  decider?: { die: number; stopStands: boolean };
  /** EZRA-S2 §3.9 — a 12 on this roll is TROUBLE in the pit, unless the decider says otherwise. */
  pitTrouble?: PitTroubleResult;
  /** B2 row 2 — this team's crewman was hurt earlier, so the check auto-fails. */
  crewInjured?: true;
}

export interface PitReorderResult extends RuleEvent {
  eventType: 'pit-reorder';
  momentTag: 'pit.box-swap' | 'pit.violation' | 'pit.no-swap';
  boxes: [number, number];
  doubles: boolean;
  violations: PitViolation[];
}

export interface FastPitResult extends RuleEvent {
  eventType: 'fast-pit';
  momentTag: 'pit.fast-pit';
  black: number;
  scope: FastPitScope;
  rolls: FastPitRoll[];
  chipDeltas: ChipDelta[];
  /** One-race interview grants spent on this pit turn (D-EZ-15). */
  grantsSpent: { driverId: string; grant: 'fastPitAuto' }[];
}

export interface ChipReorderResult extends RuleEvent {
  eventType: 'pit-chip-reorder';
  momentTag: 'pit.chip-reorder';
  /** Driver ids with at least one chip when the re-order happened. */
  chippedIds: string[];
}

export interface LeadChallengeResult extends RuleEvent {
  eventType: 'pit-lead-challenge';
  momentTag: 'pit.lead-change' | 'pit.lead-held' | 'pit.lead-held-chip' | 'pit.no-challenger';
  black: number;
  challengerId: string | null;
  leaderId: string | null;
  red: number | null;
  gasPedal: number | null;
  /** Did the red die fall inside the challenger's gas pedal? */
  gasOk: boolean;
  leadChanged: boolean;
  chipDeltas: ChipDelta[];
  /**
   * D-EZ-104 — the leader's spendable chips BEFORE the red die (after the
   * fast pit and the re-order). A presentation receipt, read by nothing that
   * decides anything: the stakes card says whether the leader can buy the
   * point back. Absent on races saved before it existed.
   */
  leaderChips?: number;
}

export interface PitTurnOptions {
  /**
   * p.xiv — the leader "may choose" to spend a chip to keep the lead. Solitaire
   * default is to spend it, in line with the same page's simplification that a
   * driver who has a chip automatically spends it to salvage a defence.
   */
  leaderSpendsChip?: boolean;
  /** p.xi — a YELLOW FLAG turn that lands on a pit turn is still conducted. */
  yellow?: boolean;
  /**
   * D-EZ-7 — EZRA Appendix B1 pit violations. Default ON. Pass `false` for the
   * base game's flat "end of the BOTTOM group", which is what RB/xi-xii prints.
   *
   * The TRIGGER is identical either way — doubles on the metallic dice, in the
   * FAST-PIT-eligible group(s). Only the penalty changes. So this toggle does
   * not change how OFTEN a violation happens, only what it costs, which is why
   * it is safe to compare the two settings on the same seed.
   */
  pitViolations?: false;
  /**
   * EZRA-S2 §3.9 — Appendix B2 pit trouble on a Fast Pit roll of 12. Default ON.
   *
   * Unlike the violations toggle this one DOES add dice (a 2d6, plus a d6 on row
   * 12), so it desynchronises every downstream roll. Expect seeded comparisons
   * across this toggle to diverge from the pit turn onward; that is the rule
   * working, not a bug.
   */
  pitTrouble?: false;
  /**
   * D-EZ-27 — EZRA's SPLIT FAST PIT grade. Default ON.
   *
   * Pass `false` for the base rulebook, where the DOUBLE FAST PIT is a separate
   * OPTIONAL second roll that can COST you the chip you just earned (RB/xvi).
   * EZRA-FP replaces that outright — "One roll - No decision" — and says the two
   * are incompatible, so this is a genuine either/or rather than a dial.
   *
   * Turning it off does NOT change the dice: the split is read off the roll that
   * was already made. A seeded race is therefore comparable across this toggle,
   * which is the opposite of `pitTrouble` above and worth knowing when reading a
   * harness.
   */
  splitFastPit?: false;
  /**
   * B2 row 2 — teams whose crewman was struck earlier in THIS race. "All
   * remaining Fast Pit checks in this race fail." It belongs to the race, not
   * the turn, so `race.ts` carries the set forward and hands it back down.
   */
  crewInjured?: readonly string[];
  /**
   * The race's laps-down memory, or absent when the optional rule is off.
   *
   * Two Appendix B1/B2 results are only exact with it: "PENALTY: 1 lap" and
   * "returns as the last driver on the lead lap". Both were degraded readings
   * for as long as the board had no laps-down sub-group, and both said so.
   */
  lapsDown?: LapsDownTracker | null;
}

export interface PitTurnResult {
  /** Every event of the pit turn, in the order it happened. */
  events: RuleEvent[];
  /** The running order after the whole procedure. */
  order: Car[];
  reorder: PitReorderResult;
  fillGap: FillGapResult | null;
  fastPit: FastPitResult;
  chipReorder: ChipReorderResult;
  leadChallenge: LeadChallengeResult;
  chipDeltas: ChipDelta[];
  violations: PitViolation[];
  leadChanged: boolean;
  /** Every Appendix B1 roll made this pit turn. */
  violationRolls: PitViolationOutcome[];
  /** Every Appendix B2 roll made this pit turn. */
  troubleRolls: PitTroubleResult[];
  /**
   * Cars the pit turn put OUT — currently only B2 row 12 on a d6 of 6. The pit
   * turn hands back a whole new order, so `turn.ts` cannot infer retirements
   * from position deltas and is told explicitly.
   */
  retired: { driverId: string; reason: NonNullable<Car['outReason']> }[];
  /** Driver ids whose crew is hurt — the running set, to feed the next pit turn. */
  crewInjured: string[];
  /** Row 51 — pairs of racers who go on the FEUD list. */
  feudIds: string[];
}

// ---------------------------------------------------------------------------
// Small pure helpers over a working order
// ---------------------------------------------------------------------------

function swapAt(order: Car[], a: number, b: number): void {
  if (a < 1 || b < 1 || a > order.length || b > order.length) return;
  const tmp = order[a - 1];
  order[a - 1] = order[b - 1];
  order[b - 1] = tmp;
}

/**
 * Add or remove chips, honouring D-EZ-28's cap of two active.
 *
 * Returns how many went to RESERVE rather than into the hand, so the caller can
 * put it on the ledger. Deliberately a return value and not module state: a
 * module-scoped accumulator would have to be cleared per turn, and a clear that
 * is ever missed bleeds one race's chips into the next — which is the precise
 * class of bug this project has already been bitten by twice.
 */
function addChips(order: Car[], driverId: string, delta: number): number {
  const i = order.findIndex((c) => c.card.driverId === driverId);
  if (i === -1) return 0;
  // D-EZ-28 — the cap of two active. A double fast pit is the likeliest way in
  // the whole game to be handed two chips at once, so this is exactly the seam
  // where the reserve rule earns its keep.
  if (delta > 0) {
    const got = gainChip(order[i], delta);
    order[i] = got.car;
    return got.toReserve;
  }
  order[i] = { ...order[i], chips: Math.max(0, order[i].chips + delta) };
  return 0;
}

// ---------------------------------------------------------------------------
// Step 1 — the metallic dice
// ---------------------------------------------------------------------------

function stepReorder(
  order: Car[],
  black: number,
  metallic: { dice: [number, number]; doubles: boolean },
  dice: Dice,
  ezra: boolean,
  yellow: boolean,
  lapsDown: LapsDownTracker | null,
  turnNo: number,
): { event: PitReorderResult; violations: PitViolation[]; outcomes: PitViolationOutcome[]; feudIds: string[] } {
  const before = [...order];
  const lapsDownOn = lapsDown !== null;
  const [a, b] = metallic.dice;
  const scope = fastPitScope(black);
  const violations: PitViolation[] = [];
  const modifiers: Modifier[] = [
    { name: 'metallic boxes', value: `${a} & ${b}`, detail: CITE.reorderDice },
    { name: 'FAST PIT scope', value: scope, detail: CITE.fastPitGroups },
  ];
  const diceRecords: DiceRecord[] = [
    {
      kind: 'd6-set',
      dice: [a, b],
      label: 'PIT TURN metallic boxes',
      faces: { boxA: a, boxB: b },
      flags: { doubles: metallic.doubles },
    },
  ];

  if (!metallic.doubles) {
    // "you'll switch the drivers in the #2 boxes with the drivers in the #5
    // boxes" — in BOTH groups. A MIDDLE box holds two cars; the odd and even
    // halves keep their sides.
    swapAt(order, TOP_BOX_POSITION[a], TOP_BOX_POSITION[b]);
    swapAt(order, MIDDLE_BOX_POSITION[a].odd, MIDDLE_BOX_POSITION[b].odd);
    swapAt(order, MIDDLE_BOX_POSITION[a].even, MIDDLE_BOX_POSITION[b].even);

    const stamped = renumber(order);
    order.splice(0, order.length, ...stamped.order);

    return {
      event: {
        eventType: 'pit-reorder',
        momentTag: 'pit.box-swap',
        ruleCitation: CITE.reorderDice,
        actorIds: diffOrder(before, order).map((d) => d.driverId),
        positionDeltas: diffOrder(before, order),
        modifiers,
        dice: diceRecords,
        description:
          `Down pit road the running order shuffles: boxes ${a} and ${b} trade places in ` +
          `both the TOP and MIDDLE groups.`,
        boxes: [a, b],
        doubles: false,
        violations: [],
      },
      violations,
      outcomes: [],
      feudIds: [],
    };
  }

  // Doubles — a pit rules violation in the box, but only where the FAST PIT
  // black die was looking.
  //
  // AMBIGUITY: on a black 6 the "group eligible for the FAST PIT" is not a group
  // at all but a list of TV drivers. Read here as: the box's occupants commit the
  // violation only if they are on the TV list, since those are the only cars the
  // black die put on pit road for scoring purposes. The alternative — nobody can
  // ever be caught on a 6 — makes a printed die face dead.
  const box = a;
  const candidates: { car: Car; group: 'top' | 'middle' }[] = [];
  if (scopeCoversGroup(scope, 'top')) {
    const car = order[TOP_BOX_POSITION[box] - 1];
    if (car) candidates.push({ car, group: 'top' });
  }
  if (scopeCoversGroup(scope, 'middle')) {
    for (const p of [MIDDLE_BOX_POSITION[box].even, MIDDLE_BOX_POSITION[box].odd]) {
      const car = order[p - 1];
      if (car) candidates.push({ car, group: 'middle' });
    }
  }
  const caught =
    scope === 'tv+leader-if-tv' ? candidates.filter((c) => c.car.tvQuality !== null) : candidates;

  for (const { car, group } of caught) {
    violations.push({
      driverId: car.card.driverId,
      group,
      box,
      position: car.position,
      chipsForfeited: car.chips - traitChipsOf(car),
    });
  }

  const outcomes: PitViolationOutcome[] = [];
  const feudIds: string[] = [];
  const deltas: PositionDelta[] = [];

  if (!ezra) {
    // BASE GAME (RB/xi-xii) — "moved to the end of the bottom group", flat, for
    // everyone caught. Chips do not travel there (p.xv).
    const caughtIds = new Set(violations.map((v) => v.driverId));
    const kept = order.filter((c) => !caughtIds.has(c.card.driverId));
    const sent = violations
      .map((v) => order.find((c) => c.card.driverId === v.driverId) as Car)
      .map((c) => parkChips(c).car);
    const stamped = renumber([...kept, ...sent]);
    order.splice(0, order.length, ...stamped.order);
    for (const v of violations) {
      deltas.push({
        driverId: v.driverId,
        from: v.position,
        to: null,
        placement: 'end-of-bottom',
        note: 'pit rules violation',
      });
    }
  } else {
    // EZRA (D-EZ-7) — the trigger is the same; Appendix B1 decides the cost.
    // One roll per caught car: two cars caught in the same MIDDLE box are two
    // separate offences on the sheet, not one shared verdict.
    for (const v of violations) {
      const roll = dice.about([v.driverId], () => rollPitViolation(dice, yellow, v.driverId));
      const map = PENALTY_PLACEMENT[roll.penalty];
      const placement: PitPlacement = yellow ? map.yellow : map.green;

      diceRecords.push({
        kind: '11-66',
        dice: [roll.dice.red, roll.dice.white],
        code: roll.code,
        label: `PIT VIOLATION (Appendix B1) — ${v.driverId}`,
        faces: { red: roll.dice.red, white: roll.dice.white },
        flags: { discretionDefault: roll.usedDiscretionDefault },
      });

      if (placement === 'laps-down') {
        // "PENALTY: 1 lap". With the LDSG off there is nowhere on the board that
        // means "a lap down", so this falls back to the old degraded reading —
        // the end of the BOTTOM group — and says so, exactly as it did before
        // the sub-group existed.
        if (lapsDownOn) {
          const d = sendLapDown(order, v.driverId);
          if (d) {
            lapsDown?.wentDown(v.driverId, turnNo);
            deltas.push({ ...d, note: `pit violation ${roll.code} — ${roll.penalty}, a lap down` });
          }
        } else {
          const from = moveToEndOfBottom(order, v.driverId);
          if (from !== null) {
            deltas.push({
              driverId: v.driverId,
              from,
              to: null,
              placement: 'end-of-bottom',
              note: `pit violation ${roll.code} — ${roll.penalty} (laps-down rule off — degraded)`,
            });
          }
        }
      } else if (placement === 'end-of-bottom') {
        const from = moveToEndOfBottom(order, v.driverId);
        if (from !== null) {
          deltas.push({
            driverId: v.driverId,
            from,
            to: null,
            placement: 'end-of-bottom',
            note: `pit violation ${roll.code} — ${roll.penalty}`,
          });
        }
      } else if (placement === 'last-in-current-group') {
        const moved = moveToLastOfGroup(order, v.driverId);
        if (moved) {
          deltas.push({
            driverId: v.driverId,
            from: moved.from,
            to: moved.to,
            note: `pit violation ${roll.code} — ${roll.penalty}, pass through under green`,
          });
        }
      }

      /*
        D-EZ-89 (c) — ROW 51 "(Roll for Feud)" IS A ROLL, NOT A FEUD.

        The sheet's Note 2 prints the die: "Roll For Feud (d6= 1-2 Feud; 3-6 No
        Feud)". The engine used to skip the die and always put the driver on the
        list. One d6 through the engine's own dice (so a hand-rolled race asks
        for it), and only a 1 or 2 starts anything. Row 51 names no competitor,
        so the hot-head goes on the FUME list — the list for a driver with a
        grudge and no named partner (p.x) — exactly as before; only the rate is
        corrected.
      */
      let feudDie: number | undefined;
      if (roll.penalty === 'feud-roll') {
        const phase = dice.phase;
        dice.phase = `${phase ?? 'PIT TURN (RB/xi)'} — PIT VIOLATION FEUD ROLL (B1 Note 2)`;
        feudDie = dice.about([v.driverId], () => dice.d6());
        dice.phase = phase;
        diceRecords.push({
          kind: 'd6',
          dice: [feudDie],
          label: `ROLL FOR FEUD (B1 Note 2: 1-2 Feud, 3-6 No Feud) — ${v.driverId}`,
          faces: { feud: feudDie },
        });
        if (feudDie <= FEUD_ROLL_MAX) feudIds.push(v.driverId);
      }

      const outcome: PitViolationOutcome = {
        roll,
        placement,
        // The lap penalty is only exact while the sub-group that gives it
        // meaning is switched on. With it off the row falls back to the old
        // reading and has to keep saying so.
        degraded:
          placement === 'laps-down' && !lapsDownOn
            ? true
            : yellow
              ? !map.printedYellow
              : !map.printedGreen,
        why: map.why,
        feudRoll: roll.penalty === 'feud-roll',
        ...(feudDie !== undefined ? { feudDie, feudStarted: feudDie <= FEUD_ROLL_MAX } : {}),
      };
      outcomes.push(outcome);
      v.chart = outcome;
      // Only a car sent to the BOTTOM stack forfeits its chips (p.xv). A
      // pass-through inside TOP or MIDDLE does not.
      if (placement !== 'end-of-bottom' && placement !== 'laps-down') v.chipsForfeited = 0;
    }
    const stamped = renumber(order);
    order.splice(0, order.length, ...stamped.order);
  }

  if (violations.length > 0) {
    modifiers.push({ name: 'pit violations', value: violations.length, detail: CITE.violation });
    if (ezra) {
      modifiers.push({
        name: 'Appendix B1 rolls',
        value: outcomes.map((o) => `${o.roll.code}:${o.roll.penalty}`).join(', '),
        detail: CITE_EZRA_PIT_TRIGGERS.violation,
      });
    }
  }

  return {
    event: {
      eventType: 'pit-reorder',
      momentTag: violations.length > 0 ? 'pit.violation' : 'pit.no-swap',
      ruleCitation: ezra ? `${CITE.violation}; ${CITE_EZRA_PIT_TRIGGERS.violation}` : CITE.violation,
      actorIds: violations.map((v) => v.driverId),
      positionDeltas: deltas,
      modifiers,
      dice: diceRecords,
      description:
        violations.length === 0
          ? `Doubles on the metallic dice, box ${box} — but the black die had the officials watching ` +
            `somewhere else. No swap, no violation.`
          : ezra
            ? `Doubles on the metallic dice — box ${box} is caught, and the officials have a chart. ` +
              outcomes
                .map((o) =>
                  o.feudDie === undefined
                    ? o.roll.description
                    : `${o.roll.description} ${
                        o.feudStarted
                          ? `The feud die shows ${o.feudDie} — that one is going to carry onto the track.`
                          : `The feud die shows ${o.feudDie} — words, and nothing more.`
                      }`,
                )
                .join(' ')
            : `Doubles on the metallic dice — box ${box} is caught for a pit rules violation. ` +
              violations
                .map((v) => `${v.driverId} is sent to the end of the BOTTOM group from P${v.position}`)
                .join('; ') +
              '.',
      boxes: [a, b],
      doubles: true,
      violations,
    },
    violations,
    outcomes,
    feudIds,
  };
}

// ---------------------------------------------------------------------------
// Step 2 — the FAST PIT
// ---------------------------------------------------------------------------

function stepFastPit(
  order: Car[],
  black: number,
  dice: Dice,
  ezraTrouble: boolean,
  crewInjured: Set<string>,
  lapsDown: LapsDownTracker | null,
  turnNo: number,
  /** D-EZ-27 — EZRA's split grade. Off = the base game's separate optional roll. */
  split: boolean,
): FastPitResult & {
  troubleRolls: PitTroubleResult[];
  retired: { driverId: string; reason: NonNullable<Car['outReason']> }[];
  newCrewInjuries: string[];
} {
  const scope = fastPitScope(black);
  const eligible = fastPitEligible(order, black);
  const rolls: FastPitRoll[] = [];
  const chipDeltas: ChipDelta[] = [];
  const diceRecords: DiceRecord[] = [];
  const troubleRolls: PitTroubleResult[] = [];
  const retired: { driverId: string; reason: NonNullable<Car['outReason']> }[] = [];
  const newCrewInjuries: string[] = [];
  const troubleDeltas: PositionDelta[] = [];
  /** One-race interview grants spent here, so the caller can strike them off. */
  const grantsSpent: { driverId: string; grant: 'fastPitAuto' }[] = [];

  for (const car of eligible) {
    const id = car.card.driverId;
    const roll = dice.about([id], () => dice.sum2d6());
    const rating = car.card.fastPit;
    // B2 note * — "All remaining Fast Pit checks in this race fail." The roll is
    // still made (the dice belong to the sequence), it just cannot succeed.
    const hurt = crewInjured.has(id);

    // EZRA-S2 §3.9 — a 12 is TROUBLE in the pit, and trouble is not a clean
    // stop. It REPLACES the outcome rather than adding to it.
    //
    // EZRA caps Fast Pit ratings at 9, so under EZRA cards a 12 could never
    // have been a hit anyway. Base-game cards carry no such cap, and without
    // this a card rated 10-12 would both earn a performance chip and have the
    // crew drop the car — the one roll paying out twice, in opposite
    // directions. Making the sentinel explicit means the rule does not quietly
    // depend on the rating cap holding everywhere.
    // D-EZ-27 — the decider die. Rolled ONLY where a 12 would have been a hit
    // as well as the trouble sentinel, which is the single case where the two
    // rules genuinely collide. Everywhere else there is nothing to decide and
    // no die is spent, so the pit-trouble rate is untouched for every card that
    // cannot clear a 12.
    const collides = ezraTrouble && roll.total === 12 && rating >= 12 && !hurt;
    let decider: { die: number; stopStands: boolean } | undefined;
    if (collides) {
      const die = dice.about([id], () => dice.d6());
      // ODD = the stop stands. EZRA's own printed parity idiom; see DECIDER_CITE.
      decider = { die, stopStands: die % 2 === 1 };
      diceRecords.push({
        kind: 'd6',
        dice: [die],
        total: die,
        label: `PIT DECIDER — ${nameOf(car)}, a 12 that is both a stop and trouble`,
        flags: { stopStands: decider.stopStands },
      });
    }
    const isTrouble = ezraTrouble && roll.total === 12 && (!collides || !decider!.stopStands);

    // POST RACE INTERVIEW — "On first ELIGIBLE pit roll for Fast Pit, Racer
    // automatically succeeds fast pit dice roll." (D-EZ-15, one race.)
    //
    // THE ROLL IS STILL MADE. The sheet says the racer succeeds, not that he
    // skips the roll, and the dice belong to the sequence — swallowing one here
    // would desynchronise every car that pits after him.
    //
    // ORDER: the grant does NOT beat pit trouble or an injured crewman. B2's
    // note * is a flat "all remaining Fast Pit checks in this race FAIL" and a
    // 12 is trouble in the pit, not a slow stop. A grant that overrode a crew
    // dropping the car would be reading "automatically succeeds" as immunity to
    // things that are not about the driver at all.
    const granted = car.grants?.fastPitAuto;
    const autoHit = granted !== undefined && !hurt && !isTrouble;
    const earned = autoHit || (!hurt && !isTrouble && roll.total <= rating);

    // EZRA's SPLIT GRADE (D-EZ-27) — the same roll, read against the second
    // threshold. No second roll and no decision: "One roll - No decision."
    //
    // `split` is off for the base game, where the DOUBLE FAST PIT is a separate
    // optional roll with a chip-losing downside (RB/xvi) that EZRA deleted.
    const doubleRating = split ? doubleFastPitGrade(car.card) : null;
    // The interview's DOUBLE grant carries the second chip by itself, so it is
    // not also read against the grade — that would pay the same driver twice
    // for the same stop.
    const doubled =
      earned && !autoHit && doubleRating !== null && roll.total <= doubleRating;

    const entry: FastPitRoll = { driverId: id, position: car.position, rating, roll: roll.total, earned };
    if (split) entry.doubleRating = doubleRating;
    if (doubled) entry.doubled = true;
    if (decider) entry.decider = decider;
    if (hurt) entry.crewInjured = true;
    if (autoHit) {
      entry.interviewGrant = granted;
      grantsSpent.push({ driverId: id, grant: 'fastPitAuto' });
    }

    diceRecords.push({
      kind: '2d6-sum',
      dice: roll.dice,
      total: roll.total,
      label: `FAST PIT — ${nameOf(car)} v ${rating}`,
      flags: { earned, crewInjured: hurt, pitTrouble: roll.total === 12 && ezraTrouble },
    });

    if (earned) {
      chipDeltas.push({ driverId: id, delta: +1, reason: 'FAST PIT (p.xii)' });
      addChips(order, id, +1);
      // "GAIN ONE DBL FAST PIT- ... Racer automatically succeeds fast pit AND
      // double fast pit dice rolls. Racer is given 2 Performance chips." The
      // two chips the sheet prints are handed over at the start of the race by
      // `interviewEffects`; the DOUBLE is the second chip here.
      if (autoHit && granted === 'double') {
        chipDeltas.push({ driverId: id, delta: +1, reason: 'DOUBLE FAST PIT — post-race interview grant' });
        addChips(order, id, +1);
      } else if (doubled) {
        chipDeltas.push({
          driverId: id,
          delta: +1,
          reason: `DOUBLE FAST PIT — ${roll.total} against a split grade of ${rating}/${doubleRating} (EZRA)`,
        });
        addChips(order, id, +1);
      }
    }

    // EZRA-S2 §3.9 — "if the resulting dice roll is 12, the Pit Crew experiences
    // TROUBLE in the pit." Resolved immediately, before the next car's roll, so
    // a car displaced by trouble is already displaced when the rest pit.
    if (isTrouble) {
      const at = order.findIndex((c) => c.card.driverId === id);
      const livePos = at === -1 ? car.position : at + 1;
      const fromGroup: Group = livePos === 1 ? 'leader' : groupOf(livePos);
      const trouble = dice.about([id], () => rollPitTrouble(dice, id, fromGroup, lapsDown !== null));
      troubleRolls.push(trouble);
      entry.pitTrouble = trouble;

      diceRecords.push({
        kind: '2d6-sum',
        dice: trouble.dice.pair,
        total: trouble.total,
        label: `PIT STOP TROUBLE (Appendix B2) — ${nameOf(car)}`,
        flags: { degraded: trouble.degraded, crewInjured: trouble.crewInjured },
      });
      if (trouble.fireRoll !== null) {
        diceRecords.push({
          kind: 'd6',
          dice: [trouble.fireRoll],
          label: `PIT FIRE damage check — ${nameOf(car)}`,
          flags: { retired: trouble.outcome === 'retired' },
        });
      }

      if (trouble.crewInjured) newCrewInjuries.push(id);

      switch (trouble.outcome) {
        case 'retired': {
          const i = order.findIndex((c) => c.card.driverId === id);
          if (i !== -1) {
            troubleDeltas.push({ driverId: id, from: i + 1, to: null, placement: 'out-of-race', note: 'pit fire' });
            order.splice(i, 1);
          }
          retired.push({ driverId: id, reason: 'trouble' });
          break;
        }
        case 'last-on-lead-lap': {
          // "returns as the last driver on the lead lap." With the sub-group
          // built this is now a real place on the board — the slot directly
          // above the laps-down marker — rather than the back of the field,
          // which is only the same thing while nobody is a lap down.
          const d = lapsDown
            ? moveToLastOfLeadLap(order, id, 'pit trouble 11 — last car on the lead lap')
            : null;
          if (d) {
            troubleDeltas.push(d);
          } else {
            const from = moveToEndOfBottom(order, id);
            if (from !== null) {
              troubleDeltas.push({ driverId: id, from, to: null, placement: 'end-of-bottom', note: 'pit trouble 11' });
            }
          }
          break;
        }
        case 'next-lower-group': {
          const i = order.findIndex((c) => c.card.driverId === id);
          if (i !== -1) {
            const g: Group = i === 0 ? 'leader' : groupOf(i + 1);
            if (g === 'bottom') {
              // Appendix B2 note ***: "if already in the bottom group driver
              // returns as the last driver in the bottom down group." The
              // bottom down group IS the LDSG, and it now exists.
              if (lapsDown) {
                const d = sendLapDown(order, id);
                if (d) {
                  lapsDown.wentDown(id, turnNo);
                  troubleDeltas.push({ ...d, note: 'pit fire — into the laps-down sub-group' });
                }
              } else {
                const from = moveToEndOfBottom(order, id);
                if (from !== null) {
                  troubleDeltas.push({ driverId: id, from, to: null, placement: 'end-of-bottom', note: 'pit fire (LDSG degraded)' });
                }
              }
            } else {
              // leader → last of TOP, top → last of MIDDLE, middle → end of BOTTOM.
              const target = g === 'leader' ? 7 : g === 'top' ? 19 : order.length;
              const from = i + 1;
              const [c] = order.splice(i, 1);
              const to = Math.min(target, order.length + 1);
              const seated = scopeChips(c, to).car;
              order.splice(to - 1, 0, seated);
              troubleDeltas.push({ driverId: id, from, to, note: 'pit fire — next group down' });
            }
          }
          break;
        }
        case 'last-in-current-group': {
          const moved = moveToLastOfGroup(order, id);
          if (moved) {
            troubleDeltas.push({ driverId: id, from: moved.from, to: moved.to, note: 'pit trouble [LPCG]' });
          }
          break;
        }
      }
      const restamp = renumber(order);
      order.splice(0, order.length, ...restamp.order);
      chipDeltas.push(...restamp.forfeits);
    }

    rolls.push(entry);
  }

  // p.xv — anybody in the stack cannot hold what he just won.
  const stamped = renumber(order);
  order.splice(0, order.length, ...stamped.order);
  chipDeltas.push(...stamped.forfeits);

  const earnedIds = rolls.filter((r) => r.earned).map((r) => r.driverId);

  return {
    eventType: 'fast-pit',
    momentTag: 'pit.fast-pit',
    ruleCitation: ezraTrouble
      ? `${CITE.fastPitGroups}; ${CITE.fastPitRoll}; ${CITE_EZRA_PIT_TRIGGERS.trouble}`
      : `${CITE.fastPitGroups}; ${CITE.fastPitRoll}`,
    actorIds: rolls.map((r) => r.driverId),
    positionDeltas: troubleDeltas,
    modifiers: [
      { name: 'black die', value: black, detail: CITE.fastPitGroups },
      ...(rolls.some((r) => r.doubled)
        ? [{
            name: 'double fast pits',
            value: rolls.filter((r) => r.doubled).length,
            note: rolls.filter((r) => r.doubled).map((r) => `${r.rating}/${r.doubleRating}`).join(', '),
            detail: CITE_SPLIT_FAST_PIT,
          }]
        : []),
      ...(rolls.some((r) => r.decider)
        ? [{
            name: 'pit decider die',
            value: rolls.filter((r) => r.decider).length,
            note: rolls
              .filter((r) => r.decider)
              .map((r) => `d6 ${r.decider!.die} — ${r.decider!.stopStands ? 'the stop stands' : 'the crew drops it'}`)
              .join('; '),
            detail: DECIDER_CITE,
          }]
        : []),
      { name: 'FAST PIT scope', value: scope },
      { name: 'cars rolling', value: rolls.length },
      { name: 'chips earned', value: earnedIds.length },
      ...(troubleRolls.length > 0
        ? [
            {
              name: 'PIT TROUBLE',
              value: troubleRolls.map((t) => `${t.total}:${t.outcome}`).join(', '),
              detail: CITE_EZRA_PIT_TRIGGERS.trouble,
            } as Modifier,
          ]
        : []),
      // The spend happened on a per-car ROLL, and only this aggregate event
      // reaches the ledger. Surfaced here so a granted stop is auditable.
      ...grantsSpent.map(
        (g) =>
          ({
            name: 'interview grant — FAST PIT',
            value: g.driverId,
            detail:
              'RWBR_postraceinterview.pdf — "On first ELIGIBLE pit roll for Fast Pit, Racer ' +
              'automatically succeeds fast pit dice roll."',
          }) as Modifier,
      ),
    ],
    dice: diceRecords,
    description:
      `Black ${black} — ${scope.replace(/[+-]/g, ' ').toUpperCase().trim()} on pit road. ` +
      (rolls.length === 0
        ? 'Nobody was eligible, so nobody gains anything.'
        : `${rolls.length} crew${rolls.length === 1 ? '' : 's'} went for it and ${earnedIds.length} ` +
          `got the car out clean with a performance chip.`) +
      (troubleRolls.length > 0 ? ' ' + troubleRolls.map((t) => t.description).join(' ') : ''),
    troubleRolls,
    retired,
    newCrewInjuries,
    grantsSpent,
    black,
    scope,
    rolls,
    chipDeltas,
  };
}

// ---------------------------------------------------------------------------
// Step 3 — chips move you up the board
// ---------------------------------------------------------------------------

/**
 * "swap all drivers who have at least ONE performance chip to the FRONT of their
 * respective groups, in descending order.  Non-chip drivers drop back."
 *
 * AMBIGUITY: "in descending order" is not "by chip count". The printed procedure
 * on p.xii is a pass from the highest box down, each chipped driver swapping with
 * the highest chip-less driver, which is exactly a stable partition: the chipped
 * cars reach the front keeping their running order among themselves, and so do
 * the rest. Two chips therefore buy no more board position than one — they buy a
 * second thing to spend later.
 */
function stepChipReorder(order: Car[]): ChipReorderResult {
  const before = [...order];
  const partition = (slice: Car[]): Car[] => [
    ...slice.filter((c) => c.chips > 0),
    ...slice.filter((c) => c.chips <= 0),
  ];

  const leader = order.slice(0, 1);
  const top = partition(order.slice(1, 7));
  const middle = partition(order.slice(7, 19));
  const bottom = order.slice(19);

  const stamped = renumber([...leader, ...top, ...middle, ...bottom]);
  order.splice(0, order.length, ...stamped.order);

  const chippedIds = [...top, ...middle].filter((c) => c.chips > 0).map((c) => c.card.driverId);
  const deltas = diffOrder(before, order);

  return {
    eventType: 'pit-chip-reorder',
    momentTag: 'pit.chip-reorder',
    ruleCitation: CITE.reorderChips,
    actorIds: deltas.map((d) => d.driverId),
    positionDeltas: deltas,
    modifiers: [
      { name: 'chipped cars moved up', value: chippedIds.length, detail: CITE.reorderChips },
      { name: 'BOTTOM group re-ordered', value: 'no', detail: 'RB/xii — "The drivers in the bottom group are NOT re-ordered."' },
    ],
    dice: [],
    description:
      chippedIds.length === 0
        ? 'No chips in the TOP or MIDDLE groups, so the field comes off pit road as it went in.'
        : `Coming off pit road, ${chippedIds.length} car${chippedIds.length === 1 ? '' : 's'} with a ` +
          `performance chip move to the front of their group and everybody else drops back.`,
    chippedIds,
  };
}

// ---------------------------------------------------------------------------
// Step 4 — the challenge for the lead
// ---------------------------------------------------------------------------

function stepLeadChallenge(order: Car[], dice: Dice, spendChip: boolean): LeadChallengeResult {
  const before = [...order];
  const black = dice.d6('black');
  const leader = order[0];
  const challenger = order[TOP_BOX_POSITION[black] - 1];
  const chipDeltas: ChipDelta[] = [];

  if (!leader || !challenger) {
    return {
      eventType: 'pit-lead-challenge',
      momentTag: 'pit.no-challenger',
      ruleCitation: CITE.leadChallenge,
      actorIds: leader ? [leader.card.driverId] : [],
      positionDeltas: [],
      modifiers: [{ name: 'black die', value: black, detail: CITE.leadChallenge }],
      dice: [{ kind: 'd6', dice: [black], label: 'PIT TURN lead-challenge box', faces: { black } }],
      description: `Black ${black} points at an empty TOP-group box — nobody comes for the lead.`,
      black,
      challengerId: null,
      leaderId: leader?.card.driverId ?? null,
      ...(leader ? { leaderChips: leader.chips } : {}),
      red: null,
      gasPedal: null,
      gasOk: false,
      leadChanged: false,
      chipDeltas,
    };
  }

  const red = dice.about([challenger.card.driverId, leader.card.driverId], () => dice.d6('red'));
  const gasPedal = challenger.card.gasPedal;
  const gasOk = red <= gasPedal;

  const diceRecords: DiceRecord[] = [
    { kind: 'd6', dice: [black], label: 'PIT TURN lead-challenge box', faces: { black } },
    { kind: 'd6', dice: [red], label: 'PIT TURN lead challenge — red v gas pedal', faces: { red } },
  ];
  const modifiers: Modifier[] = [
    { name: 'black die', value: black, detail: CITE.leadChallenge },
    { name: 'gas pedal', value: gasPedal, detail: 'no defence is rolled on a pit-turn lead challenge (p.xiv)' },
  ];

  const leaderHasChip = leader.chips > 0;
  const held = gasOk && spendChip && leaderHasChip;

  if (gasOk && !held) {
    swapAt(order, 1, TOP_BOX_POSITION[black]);
    const stamped = renumber(order);
    order.splice(0, order.length, ...stamped.order);
  }
  if (held) {
    chipDeltas.push({
      driverId: leader.card.driverId,
      delta: -1,
      reason: 'chip spent to preserve race LEADER status (p.xiv)',
    });
    addChips(order, leader.card.driverId, -1);
    modifiers.push({ name: 'leader spends a chip', value: -1, detail: CITE.leaderChip });
  }

  const momentTag: LeadChallengeResult['momentTag'] = !gasOk
    ? 'pit.lead-held'
    : held
      ? 'pit.lead-held-chip'
      : 'pit.lead-change';

  return {
    eventType: 'pit-lead-challenge',
    momentTag,
    ruleCitation: held ? `${CITE.leadChallenge}; ${CITE.leaderChip}` : CITE.leadChallenge,
    actorIds: [challenger.card.driverId, leader.card.driverId],
    positionDeltas: diffOrder(before, order),
    modifiers,
    dice: diceRecords,
    description:
      momentTag === 'pit.lead-change'
        ? `${nameOf(challenger)} beats ${nameOf(leader)} off pit road — red ${red} inside ${gasPedal} gas — and takes the lead.`
        : momentTag === 'pit.lead-held-chip'
          ? `${nameOf(challenger)} had the run — red ${red} inside ${gasPedal} gas — but ${nameOf(leader)} burns a performance chip and keeps the point.`
          : `${nameOf(challenger)} comes off pit road at ${nameOf(leader)}'s bumper — red ${red} against ${gasPedal} gas — and cannot clear him.`,
    black,
    challengerId: challenger.card.driverId,
    leaderId: leader.card.driverId,
    leaderChips: leader.chips,
    red,
    gasPedal,
    gasOk,
    leadChanged: momentTag === 'pit.lead-change',
    chipDeltas,
  };
}

// ---------------------------------------------------------------------------
// The whole pit turn
// ---------------------------------------------------------------------------

export function resolvePitTurn(
  state: RaceState,
  dice: Dice,
  options: PitTurnOptions = {},
): PitTurnResult {
  const spendChip = options.leaderSpendsChip ?? true;
  const yellow = options.yellow ?? false;
  const ezraViolations = options.pitViolations !== false;
  const ezraTrouble = options.pitTrouble !== false;
  const splitFastPit = options.splitFastPit !== false;
  const crewInjured = new Set(options.crewInjured ?? []);
  const lapsDown = options.lapsDown ?? null;
  const order = fieldFromState(state);

  // p.xiii(1) — "Roll the black die and the metallic dice together."
  const cam = dice.raceCam();
  const black = cam.black;
  const metallic = { dice: cam.metallic, total: cam.total, doubles: cam.doubles };

  const events: RuleEvent[] = [];
  const chipDeltas: ChipDelta[] = [];

  const {
    event: reorder,
    violations,
    outcomes: violationRolls,
    feudIds,
  } = stepReorder(order, black, metallic, dice, ezraViolations, yellow, lapsDown, state.turn);
  markResolved([reorder], dice.rolls);
  events.push(reorder);
  for (const v of violations) {
    if (v.chipsForfeited > 0) {
      chipDeltas.push({
        driverId: v.driverId,
        delta: -v.chipsForfeited,
        reason: 'chips cannot be carried in the BOTTOM group (p.xv)',
      });
    }
  }

  // p.xii — "Move the driver(s) to the end of the BOTTOM group, and fill the
  // open spot(s) as prescribed in the TROUBLE section."
  //
  // Only a car that actually LEFT its group leaves a gap. Under EZRA a
  // pass-through drops the car to the back of the same group and a feud roll
  // does not move it at all, so counting every violation here would invent
  // vacancies and promote cars into slots nobody vacated.
  const vacating = violations.filter((v) => !v.chart || v.chart.placement === 'end-of-bottom');
  let fillGap: FillGapResult | null = null;
  if (vacating.length > 0) {
    const vacated = {
      top: vacating.filter((v) => v.group === 'top').length,
      middle: vacating.filter((v) => v.group === 'middle').length,
    };
    fillGap = fillGaps({
      order,
      vacated,
      yellow,
      dice,
      cause: 'pit rules violation',
      leavers: vacating.map(v => ({
        driverId: v.driverId,
        name: state.grid.find(car => car.card.driverId === v.driverId)?.card.name ?? v.driverId,
        from: v.group,
        position: v.position,
        why: 'to-the-back' as const,
      })),
    });
    order.splice(0, order.length, ...fillGap.order);
    chipDeltas.push(...fillGap.chipDeltas);
    markResolved([fillGap], dice.rolls);
  events.push(fillGap);
  }

  const fastPit = stepFastPit(order, black, dice, ezraTrouble, crewInjured, lapsDown, state.turn, splitFastPit);
  markResolved([fastPit], dice.rolls);
  events.push(fastPit);
  chipDeltas.push(...fastPit.chipDeltas);
  for (const id of fastPit.newCrewInjuries) crewInjured.add(id);

  const chipReorder = stepChipReorder(order);
  markResolved([chipReorder], dice.rolls);
  events.push(chipReorder);

  const leadChallenge = stepLeadChallenge(order, dice, spendChip);
  markResolved([leadChallenge], dice.rolls);
  events.push(leadChallenge);
  chipDeltas.push(...leadChallenge.chipDeltas);

  const final = renumber(order);
  chipDeltas.push(...final.forfeits);

  return {
    events,
    order: final.order,
    reorder,
    fillGap,
    fastPit,
    chipReorder,
    leadChallenge,
    chipDeltas,
    violations,
    leadChanged: leadChallenge.leadChanged,
    violationRolls,
    troubleRolls: fastPit.troubleRolls,
    retired: fastPit.retired,
    crewInjured: [...crewInjured],
    feudIds,
  };
}
